import { safeQuerySelectorAll, safeAddEventListener } from './utils';

interface ImageOptions {
  preventRightClick?: boolean;
  preventDrag?: boolean;
  addWatermark?: boolean | string;
  targetSelector?: string; // Add target selector option
  observeDynamicContent?: boolean;
  convertToCanvas?: boolean; // Add new option to convert images to canvas
}

class ImageShield {
  private options: ImageOptions;
  private eventListeners: { element: HTMLElement; type: string; handler: EventListener }[] = [];
  private watermarkElements: HTMLDivElement[] = [];
  private mutationObserver: MutationObserver | null = null;

  constructor(options: ImageOptions = {}) {
    this.options = {
      preventRightClick: true,
      preventDrag: true,
      addWatermark: false,
      targetSelector: 'img', // Default selector
      observeDynamicContent: true, // Default to true
      convertToCanvas: false, // Default to false for backward compatibility
      ...options
    };
    
    this.initialize();
  }

  private initialize(): void {
    try {
      if (typeof document === 'undefined') return; // Skip for SSR
      
      const selector = this.options.targetSelector || 'img';
      this.protectImageElements(selector);
      
      // Observe DOM changes to protect new images
      if (this.options.observeDynamicContent) {
        this.setupMutationObserver();
      }
    } catch (error) {
      console.error('web-shield: Error initializing ImageShield', error);
    }
  }

  private protectImageElements(selector: string): void {
    try {
      safeQuerySelectorAll(selector).forEach(img => {
        if (!(img instanceof HTMLImageElement)) return;
        
        if (this.options.preventRightClick) {
          const contextHandler = (e: Event) => {
            e.preventDefault();
            return false;
          };
          
          safeAddEventListener(img, 'contextmenu', contextHandler);
          this.eventListeners.push({
            element: img,
            type: 'contextmenu',
            handler: contextHandler
          });
        }
        
        if (this.options.preventDrag) {
          img.setAttribute('draggable', 'false');
          
          const dragHandler = (e: Event) => {
            e.preventDefault();
            return false;
          };
          
          safeAddEventListener(img, 'dragstart', dragHandler);
          this.eventListeners.push({
            element: img,
            type: 'dragstart',
            handler: dragHandler
          });
        }
        
        if (this.options.addWatermark) {
          this.applyWatermark(img);
        }

        // Convert image to canvas to prevent URL access
        if (this.options.convertToCanvas) {
          this.convertImageToCanvas(img);
        }
      });
    } catch (error) {
      console.error('web-shield: Error protecting image elements', error);
    }
  }

  private applyWatermark(img: HTMLImageElement): void {
    try {
      // Create a watermark container
      const container = document.createElement('div');
      container.style.position = 'relative';
      container.style.display = 'inline-block';
      
      // Clone the image styles and replace it in the DOM
      const parent = img.parentNode;
      if (parent) {
        parent.insertBefore(container, img);
        container.appendChild(img);
        
        // Add watermark overlay
        const watermark = document.createElement('div');
        watermark.style.position = 'absolute';
        watermark.style.top = '0';
        watermark.style.left = '0';
        watermark.style.width = '100%';
        watermark.style.height = '100%';
        watermark.style.display = 'flex';
        watermark.style.alignItems = 'center';
        watermark.style.justifyContent = 'center';
        watermark.style.pointerEvents = 'none';
        watermark.style.color = 'rgba(255,255,255,0.5)';
        watermark.style.fontSize = '24px';
        watermark.style.fontWeight = 'bold';
        watermark.style.textShadow = '1px 1px 2px rgba(0,0,0,0.7)';
        watermark.style.transform = 'rotate(-45deg)';
        
        const watermarkText = typeof this.options.addWatermark === 'string' 
          ? this.options.addWatermark 
          : 'Protected';
        
        watermark.textContent = watermarkText;
        container.appendChild(watermark);
        this.watermarkElements.push(watermark);
      }
    } catch (error) {
      console.error('web-shield: Error applying watermark', error);
    }
  }

  private convertImageToCanvas(img: HTMLImageElement): void {
    try {
      // Only process already loaded images or add an event listener
      if (img.complete) {
        this.replaceWithCanvas(img);
      } else {
        // Wait for image to load before converting
        img.addEventListener('load', () => this.replaceWithCanvas(img));
      }
    } catch (error) {
      console.error('web-shield: Error converting image to canvas', error);
    }
  }

  private replaceWithCanvas(img: HTMLImageElement): void {
    try {
      // Create canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return; // Browser doesn't support 2d canvas
      
      // Match dimensions
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      
      // Copy styles and attributes
      canvas.style.width = img.clientWidth + 'px';
      canvas.style.height = img.clientHeight + 'px';
      canvas.className = img.className;
      canvas.id = img.id;
      
      // Draw image on canvas
      ctx.drawImage(img, 0, 0);
      
      // Optional: Apply watermark directly on canvas if needed
      if (this.options.addWatermark) {
        const watermarkText = typeof this.options.addWatermark === 'string' 
          ? this.options.addWatermark 
          : 'Protected';
          
        ctx.save();
        ctx.globalAlpha = 0.5;
        ctx.font = 'bold 24px sans-serif';
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.translate(canvas.width/2, canvas.height/2);
        ctx.rotate(-Math.PI / 4); // -45 degrees
        ctx.fillText(watermarkText, 0, 0);
        ctx.restore();
      }
      
      // Replace image with canvas
      if (img.parentNode) {
        img.parentNode.replaceChild(canvas, img);
      }

      // Add protection against toDataURL and getImageData
      canvas.addEventListener('contextmenu', (e) => e.preventDefault());
      
      // Override the toDataURL and getImageData methods
      const originalToDataURL = canvas.toDataURL;
      if (ctx) {
        const originalGetImageData = ctx.getImageData;
        
        // Override with functions that throw errors
        canvas.toDataURL = function() {
          console.error('web-shield: Direct image export prevented');
          return '';
        };
        
        ctx.getImageData = function() {
          console.error('web-shield: Direct pixel access prevented');
          return new ImageData(1, 1);
        };
      }
    } catch (error) {
      console.error('web-shield: Error replacing image with canvas', error);
    }
  }

  private setupMutationObserver(): void {
    try {
      if (!window.MutationObserver) return; // Skip if not supported
      
      const selector = this.options.targetSelector || 'img';
      
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if any added nodes match our selector
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if the node itself matches
                if ((node as Element).matches && (node as Element).matches(selector)) {
                  this.protectImageElements(selector);
                }
                
                // Check children that match
                if (node.hasChildNodes()) {
                  this.protectImageElements(selector);
                }
              }
            });
          }
        });
      });

      // Start observing
      this.mutationObserver.observe(document.body, { 
        childList: true,
        subtree: true
      });
    } catch (error) {
      console.error('web-shield: Error setting up mutation observer for images', error);
    }
  }

  disable(): void {
    try {
      this.eventListeners.forEach(({ element, type, handler }) => {
        try {
          element.removeEventListener(type, handler);
        } catch (error) {
          console.warn(`web-shield: Error removing ${type} event listener`, error);
        }
      });
      this.eventListeners = [];
      
      // Remove watermarks
      this.watermarkElements.forEach(el => {
        try {
          el.remove();
        } catch (error) {
          console.warn('web-shield: Error removing watermark', error);
        }
      });
      this.watermarkElements = [];
      
      // Restore draggable attribute
      safeQuerySelectorAll('img').forEach(img => {
        if (img instanceof HTMLImageElement) {
          img.removeAttribute('draggable');
        }
      });
      
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
    } catch (error) {
      console.error('web-shield: Error disabling ImageShield', error);
    }
  }
}

export default ImageShield;